import { Container, isMobileDevice, Loader } from "@volumental/common";
import { sendEvent } from "@volumental/link";
import { Suspense, useCallback, useEffect, useMemo } from "react";
import type { JSX } from "react";
import { lazyWithPreload } from "react-lazy-with-preload";
import { usageTracker, UsageTrackerEvent } from "@volumental/analytic";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client/react";
import { HistoryFlow } from "../pages/history";
import { SizeChartView } from "../pages/size-chart";
import { trackIfUserDroppedOffSite } from "../services/window-service";
import { useInputStore, useScannerStore, useSessionStore, useSurveyStore, useViewStore } from "../store";
import { AppMode, CurrentView, UserFlowType } from "../types/types";
import { FitSurvey } from "../pages/fit-survey";
import { ScannerIntro } from "../pages/intro/scanner-intro";
import { PickerView } from "../pages/picker/picker-view";
import { ScannerDesktop } from "../pages/scanner/scanner-desktop";
import { graphql } from "../api/gql";
import { getJourneyIdFromUrl } from "@volumental/journey-manager";
import { getScannerUrlWithCallbacks } from "../util/get-scanner-url";
import { hashString } from "../util/hash";
import { GeneralErrorPage } from "../pages/error/GeneralErrorPage";
import { useExitModals } from "./exit-modal-provider/use-exit-modals";
import { ExitModalsProvider } from "./exit-modal-provider/exit-modal-provider";
import { FlowNotSupported } from "./flow-not-supported";
import { StandaloneScannerAbortHandler } from "./standalone-scanner-abort-handler";
import { HeroIntro } from "../pages/intro/hero-intro";
import { StepIntroView } from "../pages/intro/step-intro-view";

const Reconstructing = lazyWithPreload(() => import("../pages/reconstructing"));
const Result = lazyWithPreload(() => import("../pages/result"));

const GetOnboardingConfigurationQuery = graphql(`
  query getOnboardingConfigurationQuery {
    configuration {
      scanner {
        allowSelfScanning
        allowAssistedScanning
      }
      features {
        recommendation {
          enableSurvey
        }
      }
    }
  }
`);

const sendCloseEvent = (): void => {
  sendEvent({
    type: "close",
    data: undefined,
  });
};

export function Navigation(): React.JSX.Element {
  const { loading, error, data } = useQuery(GetOnboardingConfigurationQuery);
  if (loading) return <Loader />;
  if (error || !data) {
    return <GeneralErrorPage errorText="Missing Onboarding configuration" onClose={sendCloseEvent} />;
  }

  const {
    scanner: { allowAssistedScanning, allowSelfScanning },
    features: {
      recommendation: { enableSurvey },
    },
  } = data.configuration;
  return (
    <ExitModalsProvider>
      <StandaloneScannerAbortHandler />
      <ViewPicker
        allowAssistedScanning={allowAssistedScanning}
        allowSelfScanning={allowSelfScanning}
        enableSurvey={enableSurvey}
      />
    </ExitModalsProvider>
  );
}

interface ViewPickerProps {
  allowAssistedScanning: boolean;
  allowSelfScanning: boolean;
  enableSurvey: boolean;
}

function ViewPicker({
  allowAssistedScanning: _allowAssistedScanning,
  allowSelfScanning: _allowSelfScanning,
  enableSurvey,
}: ViewPickerProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const {
    currentView,
    setCurrentView,
    setOnboardingCompleted: _setOnboardingCompleted,
  } = useViewStore((store) => store);

  const { clearScanID, scanID, previousPurchaseID, clearPreviousPurchaseID, subject, email } = useInputStore(
    (store) => store
  );

  const { clearSurveyStore } = useSurveyStore((store) => store);

  const { setFlowType, disableCaptureValidation } = useScannerStore((store) => store);

  const { paperType, skipOnboarding, mode, token, deviceId, session } = useSessionStore((store) => store);

  const { openSkipToRecom, requestExit } = useExitModals();

  const onClose = useCallback(() => {
    sendCloseEvent();
    if (scanID || previousPurchaseID) {
      setCurrentView(CurrentView.Result);
    } else {
      setCurrentView(CurrentView.Intro);
    }
  }, [scanID, previousPurchaseID]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent): void => {
      if (event.key === "Tab") {
        const active = event.target ?? document.activeElement;
        const nodeList = document.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const focusableElements: HTMLElement[] = [...nodeList].filter(
          (element) =>
            element instanceof HTMLElement &&
            element.tagName.toLowerCase() !== "link" &&
            element.ariaDisabled !== "true"
        ) as HTMLElement[];

        const firstElement = focusableElements.at(0);
        const lastElement = focusableElements.at(-1);
        if (!firstElement || !lastElement) {
          return;
        }
        if (event.shiftKey && active === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && active === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [currentView, onClose]);

  useEffect(() => {
    trackIfUserDroppedOffSite();
  }, []);

  useEffect(() => {
    if (currentView === CurrentView.SizePicker) {
      void Reconstructing.preload();
    } else if (currentView === CurrentView.Reconstructing) {
      void Result.preload();
    }
  }, [currentView]);

  //Handlers
  const onMobileScanClose = (): void => {
    requestExit({ onClose });
  };

  const onShowSkipToRecommendationModal = (): void => {
    if (previousPurchaseID) {
      usageTracker.track(UsageTrackerEvent.HybridLeaveOnScannerIntro, {
        sizing_flow_type: UserFlowType.Hybrid,
      });
      openSkipToRecom({
        onSkip: onSkipToRecommandation,
        onCloseExperience: () => {
          usageTracker.track(UsageTrackerEvent.ExitWithoutRecommendation, {
            sizing_flow_type: UserFlowType.Hybrid,
          });
          onMobileScanClose();
        },
      });
    } else {
      onMobileScanClose();
    }
  };

  const onSkipToRecommandation = (): void => {
    usageTracker.track(UsageTrackerEvent.SkipToResultHybrid, {
      sizing_flow_type: UserFlowType.Hybrid,
    });
    setCurrentView(CurrentView.Reconstructing);
    clearSurveyStore();
  };

  const onSkipSurvey = (): void => {
    clearPreviousPurchaseID();
    usageTracker.track(UsageTrackerEvent.SkipFitSurvey);
    setCurrentView(currentView === CurrentView.AddPPToScan ? CurrentView.Result : CurrentView.ScannerIntro);
  };

  const continueToScannerIntro = (): void => {
    clearScanID();
    setCurrentView(CurrentView.ScannerIntro);
  };
  const continueToReconstructing = (): void => {
    setCurrentView(CurrentView.Reconstructing);
    clearSurveyStore();
  };
  const continueToResult = (): void => {
    setCurrentView(CurrentView.Result);
  };
  const backToIntro = (): void => {
    setCurrentView(CurrentView.Intro);
  };
  const backToStepListIntro = (): void => {
    setCurrentView(CurrentView.StepListIntro);
    clearSurveyStore();
  };

  const redirectToScanner = async (
    scannerType: UserFlowType.Solo | UserFlowType.Assisted | UserFlowType.AssistedAdult
  ): Promise<void> => {
    const paperFormat = paperType === "us-letter" ? "letter" : (paperType ?? undefined);
    usageTracker.track(UsageTrackerEvent.RedirectToScanner, {
      sizing_flow_type: scannerType,
    });
    if (currentView) {
      try {
        sessionStorage.setItem("sidebar-pre-scanner-view", currentView);
      } catch {
        // Unavailable in privacy-restricted or sandboxed contexts; proceed without saving.
      }
    }
    const integrationId = subject ? await hashString(subject) : undefined;
    globalThis.location.href = getScannerUrlWithCallbacks(scannerType, {
      clientId: token,
      deviceId: deviceId ?? "",
      currentUrl: globalThis.location.href,
      previousPurchaseId: previousPurchaseID ?? undefined,
      session,
      paperFormat: skipOnboarding && paperFormat ? paperFormat : undefined,
      skipValidation: disableCaptureValidation,
      language: i18n.language,
      integrationId,
      journeyId: getJourneyIdFromUrl(),
      email: email ?? undefined,
    });
  };

  const continueFromPicker = async (scannerType: UserFlowType): Promise<void> => {
    if (!isMobileDevice()) {
      // Desktop: show QR code for user to scan on their phone
      setFlowType(scannerType);
      setCurrentView(CurrentView.ScannerDesktop);
      return;
    }
    await redirectToScanner(scannerType as UserFlowType.Solo | UserFlowType.Assisted | UserFlowType.AssistedAdult);
  };

  const handleCloseQrView = (): void => {
    if (currentView !== CurrentView.ScannerDesktop) {
      setCurrentView(CurrentView.Result);
      return;
    }
    if (enableSurvey) {
      onShowSkipToRecommendationModal();
      return;
    }
    onClose();
  };

  const selectedFlow = useMemo(() => {
    switch (currentView) {
      case CurrentView.HistorySelectScan: {
        return <HistoryFlow onClose={onClose} />;
      }
      case CurrentView.Intro: {
        return <HeroIntro onClose={onMobileScanClose} />;
      }
      case CurrentView.StepListIntro: {
        return <StepIntroView onClose={onMobileScanClose} onBack={backToIntro} />;
      }
      case CurrentView.AddPPToScan:
      case CurrentView.SizePicker: {
        usageTracker.track(UsageTrackerEvent.HybridSurvey, {
          sizing_flow_type: UserFlowType.Hybrid,
        });
        return (
          <FitSurvey
            onBack={backToStepListIntro}
            onSkip={onSkipSurvey}
            onClose={currentView === CurrentView.SizePicker ? onMobileScanClose : continueToResult}
            onContinue={currentView === CurrentView.SizePicker ? continueToScannerIntro : continueToReconstructing}
            submitButtonText={t("button.continue")}
          />
        );
      }

      case CurrentView.ScannerIntro: {
        usageTracker.track(UsageTrackerEvent.ScannerIntro, {
          sizing_flow_type: UserFlowType.Hybrid,
        });
        return (
          <ScannerIntro
            onBack={() => {
              setCurrentView(CurrentView.SizePicker);
            }}
            onClose={onShowSkipToRecommendationModal}
            onContinue={() => {
              setCurrentView(CurrentView.ScannerPicker);
            }}
            onSkip={previousPurchaseID ? onSkipToRecommandation : undefined}
          />
        );
      }
      case CurrentView.RecomQRcodeView:
      case CurrentView.ScannerDesktop: {
        return (
          <ScannerDesktop
            onBack={
              currentView === CurrentView.ScannerDesktop
                ? () => {
                    setCurrentView(
                      enableSurvey && mode === AppMode.Recommendation ? CurrentView.ScannerPicker : CurrentView.Picker
                    );
                  }
                : undefined
            }
            onClose={handleCloseQrView}
          />
        );
      }
      case CurrentView.Picker:
      case CurrentView.ScannerPicker: {
        return (
          <PickerView
            onBack={() => {
              setCurrentView(currentView === CurrentView.Picker ? CurrentView.Intro : CurrentView.ScannerIntro);
            }}
            onClose={currentView === CurrentView.Picker ? onMobileScanClose : onShowSkipToRecommendationModal}
            onContinue={continueFromPicker}
          />
        );
      }

      case CurrentView.Reconstructing: {
        return (
          <Suspense fallback={<Loader />}>
            <Reconstructing />
          </Suspense>
        );
      }

      case CurrentView.Result: {
        return (
          <Suspense fallback={<Loader />}>
            <Result onClose={onClose} />
          </Suspense>
        );
      }
      case CurrentView.SizeChart: {
        return <SizeChartView onClose={onClose} />;
      }
      case undefined: {
        return <FlowNotSupported />;
      }
    }
  }, [currentView, requestExit]);

  return <Container>{selectedFlow}</Container>;
}
